import React, { useCallback, useMemo } from "react"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { Table } from "resha"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import {
    FilterExpression,
    UnaryFilterOperator
} from "../../../../../Models/Client/FilterExpression"
import { superAILabelerColumns } from "./Columns/superAILabelerColumns"
import {
    getSuperAILabelerID,
    SuperAILabelerModel
} from "../../../../../Models/Client/SuperAILabelerModel"
import { Menu } from "dash-styled"
import { useSelection } from "../../../../../Hooks/useSelection"
import { copyToClipboard } from "../../../../../Helpers/Functions/clipboard"
import { useDispatch } from "react-redux"
import { successMessage } from "../../../../../Actions/Client/MessagesActions"
import {
    dateTimeFilter,
    enumFilter,
    FilterFields,
    numberFilter,
    stringFilter
} from "../../../../Common/FilterComposer/FilterComposer"
import identity from "lodash/identity"
import { SortOptionField } from "../../../../Common/Button/SortChooser"

interface Props {
    items: SuperAILabelerModel[] | undefined
    totalCount: number
    filteredCount: number
    loading?: boolean
    filters?: FilterExpression<SuperAILabelerModel>[]
    sort: SortExpression<SuperAILabelerModel>
    loadMore: () => Promise<unknown>
    toggleActive?: (ids: string[], isActive: boolean) => Promise<void>
    onChangeFilter: (value: FilterExpression<SuperAILabelerModel>[]) => void
    onChangeSort: (value: SortExpression<SuperAILabelerModel>) => void
}

const superAILabelersFilterFields: FilterFields<SuperAILabelerModel> = {
    email: stringFilter<string>(
        "Email",
        identity,
        identity,
        "",
        UnaryFilterOperator.contains,
        "contains"
    ),
    isActive: enumFilter<boolean>(
        "Status",
        {
            Active: true,
            Inactive: false
        },
        undefined,
        false
    ),
    taskCompleted: numberFilter<number>("Tasks completed", {
        toNumber: identity,
        fromNumber: identity
    }),
    taskMedianTime: numberFilter<number>("Task time (median)", {
        toNumber: identity,
        fromNumber: identity,
        largerThanLabel: "is longer than",
        smallerThanLabel: "is shorter than"
    }),
    reviewScore: numberFilter<number | undefined>("Reviewed as correct", {
        toNumber: identity,
        fromNumber: identity
    }),
    lastLabeled: dateTimeFilter("Last processed", Date.now().toString()),
    trustScore: numberFilter<number | undefined>("Trust score", {
        toNumber: identity,
        fromNumber: identity
    })
}

const superAILabelersSortFields: SortOptionField<SuperAILabelerModel>[] = [
    {
        field: "email",
        label: "Email",
        ascLabel: "Alphabetical",
        descLabel: "Reverse alphabetical"
    },
    {
        field: "isActive",
        label: "Activated",
        descLabel: "Active first",
        ascLabel: "Inactive first"
    },
    { field: "trustScore", label: "Trust score" },
    { field: "taskCompleted", label: "Tasks completed" },
    { field: "taskMedianTime", label: "Task time (median)" },
    { field: "reviewScore", label: "Reviewed as correct" },
    { field: "lastLabeled", label: "Last processed" }
]

export const SuperAILabelerTable: React.FC<Props> = ({
    items,
    totalCount,
    filteredCount,
    loadMore,
    toggleActive,
    onChangeSort,
    onChangeFilter,
    filters,
    sort
}: Props) => {
    const dispatch = useDispatch()
    const sortOptions = useMemo(
        () => ({
            fields: superAILabelersSortFields,
            applied: sort,
            onChange: onChangeSort
        }),
        [onChangeSort, sort]
    )
    const filterOptions = useMemo(
        () => ({
            applied: filters ?? [],
            fields: superAILabelersFilterFields,
            defaultOnAdd: {
                operator: UnaryFilterOperator.ge,
                fieldName: "taskMedianTime" as const,
                value: 0
            },
            onChange: onChangeFilter
        }),
        [filters, onChangeFilter]
    )
    const handleToggleActive = useMemo(
        () =>
            toggleActive === undefined
                ? undefined
                : (id: string, isActive: boolean) =>
                      toggleActive([id], isActive),
        [toggleActive]
    )
    const columns = useMemo(
        () => superAILabelerColumns({ handleToggleActive }),
        [handleToggleActive]
    )
    const [selectedItems, handleToggleSelection] = useSelection(
        getSuperAILabelerID,
        items ?? []
    )
    const actions = useMemo<[string, () => void][]>(() => {
        if (selectedItems.length === 0) {
            return []
        }

        const activatedItemIds = selectedItems.flatMap(s =>
            s.isActive ? [s.uuid] : []
        )
        const deactivatedItemIds = selectedItems.flatMap(s =>
            s.isActive ? [] : [s.uuid]
        )
        const actions: [string, () => void][] = [
            [
                "Copy selected emails",
                () => {
                    copyToClipboard(selectedItems.map(i => i.email).join("\n"))

                    dispatch(
                        successMessage(
                            selectedItems.length > 1
                                ? "Emails copied"
                                : "Email copied"
                        )
                    )
                }
            ]
        ]

        if (deactivatedItemIds.length > 0 && toggleActive !== undefined) {
            actions.push([
                "Activate selected",
                () => toggleActive(deactivatedItemIds, true)
            ])
        }

        if (activatedItemIds.length > 0 && toggleActive !== undefined) {
            actions.push([
                "Deactivate selected",
                () => toggleActive(activatedItemIds, false)
            ])
        }

        return actions
    }, [dispatch, selectedItems, toggleActive])
    const actionItems = useMemo(
        () => actions.map(action => action[0]),
        [actions]
    )
    const handleAction = useCallback(
        (selected: string) =>
            actions.find(action => action[0] === selected)?.[1](),
        [actions]
    )
    const checkedItemIds = useMemo(
        () => selectedItems.map(getSuperAILabelerID),
        [selectedItems]
    )

    return (
        <>
            <TableHeader
                totalCount={totalCount}
                sortOptions={sortOptions}
                filterOptions={filterOptions}
                filteredCount={filteredCount}
                extraActions={
                    <Menu
                        className="ml-2"
                        testId="extra-actions-menu"
                        title="Actions"
                        disabled={actionItems.length === 0}
                        items={actionItems}
                        align="right"
                        onChoose={handleAction}
                    />
                }
            />
            <Table<SuperAILabelerModel>
                columns={columns}
                items={items}
                itemCount={totalCount}
                getKey={getSuperAILabelerID}
                checkedItemIds={checkedItemIds}
                className="w-full h-0 min-h-0 mt-0"
                rowClassName="mx-6"
                loadMore={loadMore}
                renderEmpty={() => (
                    <div className="p-32 flex flex-col text-center justify-center flex-grow text-smoke-610">
                        Super.AI's in-house workers who completed
                        <br />
                        tasks for this project will be shown here
                    </div>
                )}
                onToggleRowCheck={
                    items === undefined || items.length === 0
                        ? undefined
                        : handleToggleSelection
                }
            />
        </>
    )
}
